import { Permission } from '@Constants/Permission.g';
import { AUFTRAG_API } from '@Constants/backendConstants';
import { useHasPermission } from '@Contexts/appContext';
import { useAuftragRepository } from '@Hooks/useRepository';
import type { ApiStorageTransaction } from '@Model';
import { StorageTransactionsRepository } from '@Repositories/Auftragsbearbeitung';
import {
    FrameFallback,
    type IFrameEditShellProps,
    useEditFrame,
    useFrameEditBaseNavigation,
} from '@synqo/frontend-component-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { bookingStamp, splitRefusal } from './StorageTransaction/commitRefusal';
import { StorageTransactionFrameBody } from './StorageTransaction/StorageTransactionFrameBody';

// @ai-generated 2026-10-21T16:00:24+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
type FrameProps = IFrameEditShellProps<ApiStorageTransaction> & {
    /** Called after the stored booking shown was cancelled. */
    onCancelled?: () => void;
};

export function FrameEditStorageTransaction(props: FrameProps) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    return (
        <StorageTransactionEditFrame
            key={primaryKey}
            {...props}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
        />
    );
}

/** A stored booking never changes, so the frame only creates and shows. */
// @ai-generated 2026-10-21T16:00:24+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
function StorageTransactionEditFrame(props: FrameProps & { primaryKey: string }) {
    const hasPermission = useHasPermission();
    const repository = useAuftragRepository(StorageTransactionsRepository);
    const [refusal, setRefusal] = useState<{ message: string; stamp: string }>();
    const latestBooking = useRef<ApiStorageTransaction>(undefined);

    const commit = useCallback(
        async (draftId: string) => {
            const outcome = splitRefusal(await repository.commitDraft(draftId));
            const booking = latestBooking.current;
            setRefusal(
                outcome.refusal !== undefined && booking
                    ? { message: outcome.refusal, stamp: bookingStamp(booking) }
                    : undefined,
            );
            return outcome.result;
        },
        [repository],
    );

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    const events = useMemo(
        () => ({
            ...frameEditBaseNavigationEvents,
            ...props.events,
        }),
        [frameEditBaseNavigationEvents, props.events],
    );

    const frame = useEditFrame<ApiStorageTransaction>({
        apiResourcePath: `${AUFTRAG_API}/storage_transactions`,
        primaryKey: props.primaryKey,
        embedded: props.embedded,
        events,
        canEdit: false,
        canCreate: props.canCreate !== false && hasPermission(Permission.AuftragsbearbeitungStorageTransactionsCreate),
        canDelete: false,
        commit,
    });

    const readyBooking = frame.status === 'ready' ? frame.data : undefined;
    useEffect(() => {
        latestBooking.current = readyBooking;
    }, [readyBooking]);

    if (frame.status !== 'ready') {
        return <FrameFallback frame={frame} />;
    }

    const rejection = refusal?.stamp === bookingStamp(frame.data) ? refusal.message : undefined;

    return (
        <StorageTransactionFrameBody
            key={frame.recordId}
            frame={frame}
            rejection={rejection}
            onCancelled={props.onCancelled}
        />
    );
}
